import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { captureException } from '@sentry/react';
import { isUnauthenticatedUrl } from '@app/constants/axios';
import { getAccessToken, refreshToken, oktaAuth } from '@app/helpers/auth/oidc';
import navigationService from '@app/services/navigationService';
import {
  ensureBennieSession,
  exchangeBennieSession,
  getOktaAccessToken,
} from '@app/helpers/bennie-session';
import {
  getOriginatorHeaders,
  ORIGINATOR_HEADER,
  ORIGINATOR_VERSION_HEADER,
} from './originatorHeaders';

const handleAuthFailure = () => {
  // Use navigation service to avoid circular dependency
  navigationService.triggerAuthFailure();
};

const getRequestUrl = args =>
  typeof args === 'string' ? args : args?.url ?? '';

const withOmittedCredentials = args =>
  typeof args === 'string'
    ? { url: args, credentials: 'omit' }
    : { ...args, credentials: 'omit' };

export const createBaseQueryWithReAuth = () => {
  const baseQuery = fetchBaseQuery({
    baseUrl: '/',
    credentials: 'include',
    prepareHeaders: async (headers, { arg }) => {
      const requestUrl = getRequestUrl(arg);

      if (isUnauthenticatedUrl(requestUrl)) {
        headers.delete('Authorization');
        headers.delete(ORIGINATOR_HEADER);
        headers.delete(ORIGINATOR_VERSION_HEADER);
        return headers;
      }

      const accessToken = await getAccessToken();

      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      } else {
        headers.delete('Authorization');
      }

      const originatorHeaders = getOriginatorHeaders();
      Object.entries(originatorHeaders).forEach(([key, value]) => {
        headers.set(key, value);
      });
      headers.set('content-type', 'application/json');

      return headers;
    },
  });

  const baseQueryWithReAuth = async (args, api, extraOptions) => {
    const requestUrl = getRequestUrl(args);
    const isUnauthenticated = isUnauthenticatedUrl(requestUrl);

    if (!isUnauthenticated) {
      // Ensure the Bennie session cookie exists before issuing the request.
      // A failure here is non-fatal: it must not block the request, and the 401
      // re-auth path below recovers a missing/expired session.
      try {
        await ensureBennieSession(oktaAuth);
      } catch (error) {
        captureException(error, {
          tags: { context: 'bennie_session_exchange', reason: 'initial_ensure_rtk' },
        });
      }
    }

    let result = await baseQuery(
      isUnauthenticated ? withOmittedCredentials(args) : args,
      api,
      extraOptions,
    );

    if (isUnauthenticated) {
      return result;
    }

    if (result.error?.status === 403) {
      handleAuthFailure();
      return result;
    }

    if (result.error?.status === 401) {
      try {
        // First try re-exchanging the session cookie (it may have expired). A
        // failure here is non-fatal — fall through to renewing the Okta token
        // rather than logging the user out prematurely.
        try {
          await exchangeBennieSession(() => getOktaAccessToken(oktaAuth));
          result = await baseQuery(args, api, extraOptions);
        } catch (exchangeError) {
          captureException(exchangeError, {
            tags: {
              context: 'bennie_session_exchange',
              reason: 'reauth_exchange_rtk',
            },
          });
        }

        if (result.error?.status === 401) {
          await refreshToken();
          result = await baseQuery(args, api, extraOptions);
        }

        // If we still get 401 after re-exchange and refresh, auth has failed
        if (result.error?.status === 401) {
          // eslint-disable-next-line no-console
          console.error(
            'Authentication failed after session exchange and token refresh, redirecting to logout',
          );
          handleAuthFailure();
          return result;
        }
      } catch (error) {
        // Only the Okta token refresh can reach this catch; the session
        // exchange is handled above. `result` still holds the failing 401
        // response, so returning it surfaces the original error to the caller.
        // eslint-disable-next-line no-console
        console.error(
          'Failed to refresh Bennie session, redirecting to logout:',
          error,
        );
        handleAuthFailure();
        return result;
      }
    }

    return result;
  };

  return baseQueryWithReAuth;
};
